import { useEffect, useRef } from 'react';
import { useBreakpoint } from '@utils/responsive';
import './Drawer.css';

/**
 * Unified Drawer Component
 * Supports left, right, and bottom positioning with responsive behavior
 *
 * @param {Object} props
 * @param {'left' | 'right' | 'bottom'} props.side - Which side the drawer slides from
 * @param {boolean} props.isOpen - Whether drawer is open
 * @param {function} props.onToggle - Handler for toggle (open/close)
 * @param {string} props.alwaysVisibleAbove - Breakpoint above which drawer is always visible (no toggle)
 *                                            Options: 'mobile', 'tablet', 'desktop', 'none'
 * @param {ReactNode} props.children - Content to render in drawer
 */
export default function Drawer({
  side = 'left',
  isOpen = true,
  onToggle,
  alwaysVisibleAbove = 'none',
  children
}) {
  const drawerRef = useRef(null);
  const breakpoint = useBreakpoint();

  // Determine if drawer should be always visible (no toggle needed)
  const breakpointOrder = ['portrait', 'mobile', 'tablet', 'desktop', 'wide'];
  const currentBreakpointIndex = breakpointOrder.indexOf(breakpoint);
  const thresholdIndex = alwaysVisibleAbove === 'none'
    ? 999
    : breakpointOrder.indexOf(alwaysVisibleAbove);

  const isAlwaysVisible = currentBreakpointIndex >= thresholdIndex;
  const effectiveIsOpen = isAlwaysVisible || isOpen;
  const showToggle = !isAlwaysVisible;

  // NOTE: ESC key handling moved to useShortcutStore (centralized keyboard shortcuts)

  // Swipe gesture handling for touch devices
  useEffect(() => {
    if (!showToggle) return;

    const drawer = drawerRef.current;
    if (!drawer) return;

    let startX = 0;
    let startY = 0;

    const handleTouchStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };

    const handleTouchEnd = (e) => {
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const deltaX = endX - startX;
      const deltaY = endY - startY;
      const threshold = 50;

      // Only process if it's a clear swipe (not a tap)
      if (Math.abs(deltaX) < threshold && Math.abs(deltaY) < threshold) return;

      if (side === 'bottom') {
        // Swipe down = close
        if (isOpen && deltaY > threshold && Math.abs(deltaY) > Math.abs(deltaX)) {
          onToggle?.();
        }
      } else if (side === 'right') {
        // Swipe right = close
        if (isOpen && deltaX > threshold && Math.abs(deltaX) > Math.abs(deltaY)) {
          onToggle?.();
        }
      } else {
        // Left drawer: swipe left = close
        if (isOpen && deltaX < -threshold && Math.abs(deltaX) > Math.abs(deltaY)) {
          onToggle?.();
        }
      }
    };

    drawer.addEventListener('touchstart', handleTouchStart, { passive: true });
    drawer.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      drawer.removeEventListener('touchstart', handleTouchStart);
      drawer.removeEventListener('touchend', handleTouchEnd);
    };
  }, [side, isOpen, onToggle, showToggle]);

  // Get chevron based on side and state
  const getChevron = () => {
    if (side === 'bottom') {
      return effectiveIsOpen ? '▼' : '▲';
    } else if (side === 'right') {
      return effectiveIsOpen ? '›' : '‹';
    } else {
      return effectiveIsOpen ? '‹' : '›';
    }
  };

  // Toggle button element
  const toggleButton = showToggle && (
    <button
      className={`drawer__toggle drawer__toggle--${side} ${effectiveIsOpen ? 'drawer__toggle--open' : ''}`}
      onClick={onToggle}
      aria-label={effectiveIsOpen ? 'Close drawer' : 'Open drawer'}
      type="button"
      data-sfx="button"
    >
      <span className="drawer__toggle-chevron">
        {getChevron()}
      </span>
    </button>
  );

  return (
    <div className={`drawer-container drawer-container--${side}`}>
      {/* Drawer Panel */}
      <div
        ref={drawerRef}
        className={`drawer drawer--${side} ${effectiveIsOpen ? 'drawer--open' : ''} ${isAlwaysVisible ? 'drawer--always-visible' : ''}`}
        aria-hidden={!effectiveIsOpen}
      >
        {/* Toggle Button - Always inside drawer, moves with it */}
        {toggleButton}

        <div className="drawer__content">
          {children}
        </div>
      </div>
    </div>
  );
}
